import { Fragment, useEffect, useRef, useState, type FormEvent } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  cancelRun,
  createRun,
  decideApproval,
  getRun,
  KEEPALIVE,
  listApprovals,
  listPolicies,
  listProjects,
  listRuns,
  updatePolicy,
} from '../lifeos/api.js';
import {
  APPROVAL_KIND_LABEL,
  approvalFacts,
  approvalSummary,
  hardStopLabel,
  RISK_TONE,
  runGroup,
  runTone,
  TASK_TYPES,
  taskTypeLabel,
  type RunGroup,
} from '../lifeos/agents.js';
import { AGENT_LEVEL_LABEL, approvalCode, humanize, levelLabel, plural, runCode, since, stamp } from '../lifeos/format.js';
import Structured from '../lifeos/Structured.js';
import { errorText, useToast } from '../lifeos/toast.js';
import { Badge, Collapsible, Loader, SafeLink, Segmented } from '../lifeos/ui.js';
import { Button, EmptyState, Group, Icon, Page, Sheet } from '../ui/index.js';
import { useLoad, type Loaded } from '../lifeos/useLoad.js';
import type {
  AgentPolicy,
  AgentRunListRow,
  ApprovalDecision,
  ApprovalKind,
  ApprovalWithProject,
  Project,
} from '../lifeos/types.js';

type Tab = 'approvals' | 'runs' | 'policies';
const TABS: readonly Tab[] = ['approvals', 'runs', 'policies'];

const DECIDED: Record<ApprovalDecision, string> = {
  approve: 'Approved',
  approve_with_changes: 'Approved with changes',
  reject: 'Rejected',
};

/**
 * Agents (`/agents`, spec §6): approvals, runs and autonomy policies.
 * `?approval=<id>` opens the approvals tab on that card — the link Signal and
 * the MCP `decide_approval` refusal point to, because approving happens only
 * here or over Signal (D14).
 */
export default function Agents(): JSX.Element {
  const [params, setParams] = useSearchParams();
  const approvalId = params.get('approval');
  const requested = params.get('tab') as Tab | null;
  const tab: Tab = approvalId ? 'approvals' : requested && TABS.includes(requested) ? requested : 'approvals';
  const approvals = useLoad(() => listApprovals({ status: 'pending' }), []);

  const n = approvals.data?.length;
  return (
    <Page
      title="Agents"
      className="bc-agents"
      sub={n === undefined ? undefined : n === 0 ? 'Nothing waiting on you' : `${n} waiting on you`}
    >
      <Segmented
        label="Agents"
        value={tab}
        options={[
          { value: 'approvals', label: 'Approvals', count: approvals.data?.length },
          { value: 'runs', label: 'Runs' },
          { value: 'policies', label: 'Policies' },
        ]}
        onChange={(t) => setParams(t === 'approvals' ? {} : { tab: t }, { replace: true })}
      />
      {tab === 'approvals' && <Approvals state={approvals} focusId={approvalId} />}
      {tab === 'runs' && <Runs />}
      {tab === 'policies' && <Policies />}
    </Page>
  );
}

// --- Approvals ---------------------------------------------------------------------------------

function Approvals({
  state,
  focusId,
}: {
  state: Loaded<ApprovalWithProject[]>;
  focusId: string | null;
}): JSX.Element {
  const toast = useToast();
  const [hidden, setHidden] = useState<ReadonlySet<string>>(new Set());

  function decide(a: ApprovalWithProject, decision: ApprovalDecision, note?: string): void {
    const code = approvalCode(a.shortCode);
    const unhide = (): void =>
      setHidden((s) => {
        const n = new Set(s);
        n.delete(a.id);
        return n;
      });
    setHidden((s) => new Set(s).add(a.id));
    toast.defer({
      text: `${DECIDED[decision]} ${code}`,
      commit: () => decideApproval(a.id, decision, note, KEEPALIVE),
      onUndo: unhide,
      onDone: () => void state.reload().finally(unhide),
      onError: unhide,
      failText: `Couldn't decide ${code}`,
    });
  }

  return (
    <Loader state={state}>
      {(items) => {
        const rows = items.filter((a) => !hidden.has(a.id));
        const missing = focusId !== null && !items.some((a) => a.id === focusId);
        return (
          <>
            {missing && (
              <p className="bc-note" role="note">
                <Icon name="check" size={18} />
                That approval is no longer pending.
              </p>
            )}
            {rows.length === 0 ? (
              <EmptyState icon="check" title="Nothing to approve" hint="Plans and ships that need you show up here." />
            ) : (
              <ul className="bc-cards">
                {rows.map((a) => (
                  <ApprovalCard key={a.id} a={a} focused={a.id === focusId} onDecide={(d, n) => decide(a, d, n)} />
                ))}
              </ul>
            )}
          </>
        );
      }}
    </Loader>
  );
}

function ApprovalCard({
  a,
  focused,
  onDecide,
}: {
  a: ApprovalWithProject;
  focused: boolean;
  onDecide: (d: ApprovalDecision, note?: string) => void;
}): JSX.Element {
  const ref = useRef<HTMLLIElement>(null);
  const [mode, setMode] = useState<null | 'approve_with_changes' | 'reject'>(null);
  const [note, setNote] = useState('');
  const facts = approvalFacts(a.payload);
  const summary = approvalSummary(a.summary, {
    project: a.project?.name ?? null,
    risk: a.risk,
    diffStat: facts.diffStat,
    hardStop: facts.hardStop,
  });

  useEffect(() => {
    if (focused) ref.current?.scrollIntoView?.({ block: 'start' });
  }, [focused]);

  function submit(e: FormEvent): void {
    e.preventDefault();
    if (!mode) return;
    if (mode === 'approve_with_changes' && !note.trim()) return;
    onDecide(mode, note.trim() || undefined);
  }

  return (
    <li ref={ref} id={`approval-${a.id}`} className={`lo-card lo-appr bc-appr${focused ? ' lo-card--focus' : ''}`}>
      <div className="lo-card__meta bc-appr__meta">
        <span className="lo-code">{approvalCode(a.shortCode)}</span>
        <span aria-hidden="true">·</span>
        <span>{APPROVAL_KIND_LABEL[a.kind]}</span>
        <span aria-hidden="true">·</span>
        <span className={`bc-risk bc-risk--${RISK_TONE[a.risk]}`}>{a.risk} risk</span>
        {a.project && <Link to={`/projects/${a.project.id}`} className="lo-card__project">{a.project.name}</Link>}
      </div>
      <h3 className="lo-card__title">{a.title}</h3>
      {summary && <p className="lo-card__body">{summary}</p>}
      {facts.hardStop && (
        <p className="lo-hardstop bc-hardstop" role="note">
          <Icon name="alert" size={18} />
          <span>Hard stop{facts.reasons.length > 0 && `: ${facts.reasons.map(hardStopLabel).join(', ')}`}</span>
        </p>
      )}
      {(facts.commitSha || facts.diffStat || facts.stagingUrl || a.expiresAt) && (
        <dl className="lo-facts bc-facts">
          {facts.commitSha && (
            <div>
              <dt>Commit</dt>
              <dd className="lo-code">{facts.commitSha.slice(0, 10)}</dd>
            </div>
          )}
          {facts.diffStat && (
            <div>
              <dt>Diff</dt>
              <dd className="lo-code">{facts.diffStat}</dd>
            </div>
          )}
          {facts.stagingUrl && (
            <div>
              <dt>Staging</dt>
              <dd>
                <SafeLink href={facts.stagingUrl} />
              </dd>
            </div>
          )}
          {a.expiresAt && (
            <div>
              <dt>Expires</dt>
              <dd>{stamp(a.expiresAt)}</dd>
            </div>
          )}
        </dl>
      )}
      {facts.proposal && (
        <blockquote className="lo-untrusted bc-quote">
          <span className="lo-untrusted__label">
            {facts.proposal.viaMcp ? (
              <>
                <Badge tone="warn">via MCP client</Badge> {facts.proposal.from?.replace(/^mcp:/, '') ?? 'unknown client'}
              </>
            ) : (
              <>
                Request from {facts.proposal.from ?? 'unknown sender'}
                {!facts.proposal.verified && ' · unverified sender'}
              </>
            )}
          </span>
          {facts.proposal.text}
        </blockquote>
      )}
      {a.runId && <RunPreview runId={a.runId} kind={a.kind} />}
      {mode === null ? (
        <div className="bc-appr__actions">
          <Button size="lg" icon="check" className="bc-appr__approve" onClick={() => onDecide('approve')}>
            Approve
          </Button>
          <Button variant="secondary" onClick={() => setMode('approve_with_changes')}>
            With changes
          </Button>
          <Button variant="destructive" onClick={() => setMode('reject')}>
            Reject
          </Button>
        </div>
      ) : (
        <form className="lo-decide bc-decide" onSubmit={submit}>
          <label className="bc-decide__field">
            <span className="bc-decide__label">{mode === 'reject' ? 'Why not? (optional)' : 'What should change?'}</span>
            <textarea
              className="bc-textarea"
              rows={3}
              value={note}
              autoFocus
              onChange={(e) => setNote(e.target.value)}
              required={mode === 'approve_with_changes'}
            />
          </label>
          <div className="bc-appr__actions">
            <Button
              type="submit"
              size="lg"
              variant={mode === 'reject' ? 'destructive' : 'primary'}
              className={mode === 'reject' ? 'bc-btn--solid-bad' : undefined}
              disabled={mode === 'approve_with_changes' && !note.trim()}
            >
              {mode === 'reject' ? 'Reject' : 'Approve with changes'}
            </Button>
            <Button variant="plain" onClick={() => setMode(null)}>
              Back
            </Button>
          </div>
        </form>
      )}
    </li>
  );
}

/**
 * The run an approval decides on, loaded with the card so nothing is approved
 * blind: the plan for a plan approval, the review (else the plan) for a ship
 * approval, and a link to the whole run with its log.
 */
function RunPreview({ runId, kind }: { runId: string; kind: ApprovalKind }): JSX.Element {
  const run = useLoad(() => getRun(runId), [runId]);
  const r = run.data;
  const isReview = kind === 'agent_ship' && r?.review != null;
  const shown = isReview ? r?.review : r?.plan;
  const label = isReview ? 'Review' : 'Plan';
  return (
    <section className="lo-appr__run bc-run" aria-label={label}>
      <span className="lo-appr__label bc-run__label">{label}</span>
      {shown != null ? (
        <Structured value={shown} bare />
      ) : (
        <p className="lo-foot">{run.error ? "Couldn't load the plan." : run.loading ? 'Loading the plan…' : 'No plan yet.'}</p>
      )}
      <Link to={`/agents/runs/${runId}`} className="btn btn--ghost btn--sm lo-appr__open">
        Open run{r ? ` ${runCode(r.shortCode)}` : ''} →
      </Link>
    </section>
  );
}

// --- Runs ------------------------------------------------------------------------------------------

const GROUPS: readonly { key: RunGroup; label: string }[] = [
  { key: 'needs_you', label: 'Needs you' },
  { key: 'running', label: 'Running' },
  { key: 'finished', label: 'Finished' },
];

function Runs(): JSX.Element {
  const runs = useLoad(() => listRuns(), []);
  const projects = useLoad(() => listProjects(), []);
  const [starting, setStarting] = useState(false);
  const toast = useToast();
  const now = new Date();
  const projectName = (id: string | null): string =>
    projects.data?.find((p) => p.id === id)?.name ?? (id ? 'Project' : 'No project');

  return (
    <>
      <div className="bc-tabbar">
        <Button variant="secondary" size="sm" icon="plus" onClick={() => setStarting(true)} aria-expanded={starting}>
          Start a run
        </Button>
      </div>
      {starting && (
        <StartRun
          projects={(projects.data ?? []).filter((p) => p.status === 'active')}
          onClose={() => setStarting(false)}
          onStarted={(code, id) => {
            setStarting(false);
            toast.undoable({ text: `Started ${runCode(code)}`, undo: () => cancelRun(id), onUndone: () => void runs.reload() });
            void runs.reload();
          }}
        />
      )}
      <Loader state={runs}>
        {(items) => {
          if (items.length === 0) return <EmptyState icon="bot" title="No runs yet" hint="Approved change requests become runs." />;
          return (
            <>
              {GROUPS.map((g) => {
                const rows = items.filter((r) => runGroup(r.status) === g.key);
                return (
                  <Group key={g.key} header={<>{g.label} <span className="bc-count">{rows.length}</span></>} label={g.label}>
                    {rows.length > 0 && (
                      <Collapsible
                        items={rows}
                        n={g.key === 'finished' ? 3 : 10}
                        render={(r: AgentRunListRow) => (
                          <li key={r.id} className="ui-swipe">
                            <div className="ui-row ui-row--tap">
                              <span className="ui-row__lead">
                                <span className={`bc-status bc-status--${runTone(r.status) === 'accent' ? 'run' : runTone(r.status) === 'muted' ? 'idle' : runTone(r.status)}`} aria-hidden="true" />
                              </span>
                              <Link to={`/agents/runs/${r.id}`} className="ui-row__main">
                                <span className="ui-row__text">
                                  <span className="ui-row__title lo-row__title">
                                    <span className="lo-code">{runCode(r.shortCode)}</span> {taskTypeLabel(r.taskType)}
                                  </span>
                                  <span className="ui-row__sub">
                                    {humanize(r.status)} · {projectName(r.projectId)}
                                    {r.dryRun && ' · dry run'}
                                  </span>
                                </span>
                                <span className="ui-row__detail bc-detail-sm">{since(r.updatedAt, now)}</span>
                                <Icon name="chevron-right" size={18} className="ui-row__chev" />
                              </Link>
                            </div>
                          </li>
                        )}
                      />
                    )}
                  </Group>
                );
              })}
            </>
          );
        }}
      </Loader>
    </>
  );
}

function StartRun({
  projects,
  onStarted,
  onClose,
}: {
  projects: Project[];
  onStarted: (shortCode: number, id: string) => void;
  onClose: () => void;
}): JSX.Element {
  const [projectId, setProjectId] = useState('');
  const [taskType, setTaskType] = useState(TASK_TYPES[0]!);
  const [text, setText] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit(e?: FormEvent): Promise<void> {
    e?.preventDefault();
    if (!projectId || !text.trim()) {
      setErr('Pick a project and describe the change.');
      return;
    }
    setBusy(true);
    try {
      const run = await createRun({ projectId, taskType, text: text.trim() });
      onStarted(run.shortCode, run.id);
    } catch (error) {
      setErr(errorText(error));
      setBusy(false);
    }
  }

  return (
    <Sheet title="Start a run" onClose={onClose}>
      <form className="bc-sheetform" onSubmit={(e) => void submit(e)} aria-label="Start a run">
        <ul className="ui-group bc-inset">
          <li className="ui-swipe">
            <label className="bc-field">
              <span className="bc-field__label">Project</span>
              <select className="bc-field__input" value={projectId} onChange={(e) => setProjectId(e.target.value)}>
                <option value="">Choose…</option>
                {projects.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                  </option>
                ))}
              </select>
            </label>
          </li>
          <li className="ui-swipe">
            <label className="bc-field">
              <span className="bc-field__label">Kind</span>
              <select className="bc-field__input" value={taskType} onChange={(e) => setTaskType(e.target.value)}>
                {TASK_TYPES.map((t) => (
                  <option key={t} value={t}>
                    {taskTypeLabel(t)}
                  </option>
                ))}
              </select>
            </label>
          </li>
          <li className="ui-swipe">
            <label className="bc-field bc-field--stack">
              <span className="bc-field__label">What should change?</span>
              <textarea className="bc-field__input" rows={3} value={text} onChange={(e) => setText(e.target.value)} />
            </label>
          </li>
        </ul>
        {err && (
          <p className="bc-formerr" role="alert">
            {err}
          </p>
        )}
        <Button type="submit" size="lg" block disabled={busy}>
          {busy ? 'Starting…' : 'Start run'}
        </Button>
      </form>
    </Sheet>
  );
}

// --- Policies --------------------------------------------------------------------------------------

/**
 * Policies grouped by task type: the default row first, then each project's
 * override indented under it (labelled with the project only). Order follows
 * the server's list.
 */
export function groupPolicies(items: readonly AgentPolicy[]): { taskType: string; base: AgentPolicy | null; overrides: AgentPolicy[] }[] {
  const order: string[] = [];
  const by = new Map<string, AgentPolicy[]>();
  for (const p of items) {
    if (!by.has(p.taskType)) {
      by.set(p.taskType, []);
      order.push(p.taskType);
    }
    by.get(p.taskType)!.push(p);
  }
  return order.map((taskType) => {
    const rows = by.get(taskType)!;
    return { taskType, base: rows.find((p) => p.projectId === null) ?? null, overrides: rows.filter((p) => p.projectId !== null) };
  });
}

/** The track record, only once there is one. */
export function policyStats(p: Pick<AgentPolicy, 'streak' | 'approvalsTotal' | 'rejectionsTotal'>): string | null {
  if (p.approvalsTotal + p.rejectionsTotal === 0) return null;
  if (p.streak > 0) return `${p.streak} approved in a row`;
  return `${plural(p.approvalsTotal, 'approval')}, ${plural(p.rejectionsTotal, 'rejection')}`;
}

function Policies(): JSX.Element {
  const policies = useLoad(listPolicies, []);
  const projects = useLoad(() => listProjects(), []);
  const toast = useToast();
  const [pending, setPending] = useState<Record<string, number>>({});

  const clear = (id: string) => (): void =>
    setPending((p) => {
      const n = { ...p };
      delete n[id];
      return n;
    });

  function change(p: AgentPolicy, level: number): void {
    setPending((x) => ({ ...x, [p.id]: level }));
    toast.defer({
      text: `${taskTypeLabel(p.taskType)} → ${levelLabel(level)}`,
      commit: () => updatePolicy(p.id, level, KEEPALIVE),
      key: `policy:${p.id}`,
      onUndo: () => {
        clear(p.id)();
        void policies.reload();
      },
      onDone: () => void policies.reload().finally(clear(p.id)),
      onError: clear(p.id),
      failText: `Couldn't change ${taskTypeLabel(p.taskType)}`,
    });
  }

  function row(p: AgentPolicy, override: boolean): JSX.Element {
    const project = projects.data?.find((x) => x.id === p.projectId) ?? p.project;
    const ceiling = project?.autonomyCeiling ?? 4;
    const level = pending[p.id] ?? p.level;
    const type = taskTypeLabel(p.taskType);
    const name = override ? (project?.name ?? 'Project') : p.projectId ? `${type} · ${project?.name ?? 'Project'}` : type;
    const sub = [override && ceiling < 4 ? `ceiling L${ceiling}` : null, policyStats(p)].filter(Boolean).join(' · ');
    return (
      <li key={p.id} className={`lo-row lo-policy bc-policy${override ? ' lo-policy--override' : ''}`}>
        <div className="lo-row__main">
          <span className="lo-row__title">{name}</span>
          {sub && <span className="lo-row__sub">{sub}</span>}
        </div>
        <select
          className="lo-level bc-level"
          aria-label={`Level for ${type}${override && project ? ` on ${project.name}` : ''}`}
          value={level}
          onChange={(e) => change(p, Number(e.target.value))}
        >
          {AGENT_LEVEL_LABEL.map((_, l) => (
            <option key={l} value={l} disabled={l > ceiling}>
              {levelLabel(l)}
            </option>
          ))}
        </select>
      </li>
    );
  }

  return (
    <Loader state={policies}>
      {(items) =>
        items.length === 0 ? (
          <EmptyState icon="shield" title="No policies" />
        ) : (
          <>
            <ul className="lo-list bc-list">
              {groupPolicies(items).map((g) => (
                <Fragment key={g.taskType}>
                  {g.base && row(g.base, false)}
                  {g.overrides.map((p) => row(p, g.base !== null))}
                </Fragment>
              ))}
            </ul>
            <p className="ui-group__foot bc-foot">
              Ten approvals in a row move a kind of change up one level, never past the project's ceiling. A rejection moves it
              down.
            </p>
            <details className="lo-levels bc-levels">
              <summary>
                What the levels mean <Icon name="chevron-down" size={16} />
              </summary>
              <dl className="lo-facts bc-facts">
                {AGENT_LEVEL_LABEL.map((label, l) => (
                  <LevelFact key={l} level={l} label={label} />
                ))}
              </dl>
            </details>
          </>
        )
      }
    </Loader>
  );
}

function LevelFact({ level, label }: { level: number; label: string }): JSX.Element {
  const [name, what] = label.split(' — ');
  return (
    <div>
      <dt>
        L{level} · {name}
      </dt>
      <dd>{what ?? ''}</dd>
    </div>
  );
}
